import type { Snippet } from 'svelte'
import { ButtonType } from '@repo/commons/components'

interface ShowOptions {
	title?: string
	message?: string
	content?: Snippet
	confirmText?: string
	cancelText?: string
	confirmButtonType?: ButtonType
}

interface ConfirmationState {
	visible: boolean
	title: string
	message: string
	content: Snippet | null
	confirmText: string
	cancelText: string
	confirmButtonType: ButtonType
	onConfirm: (() => void) | null
	onCancel: (() => void) | null
}

class ConfirmationStore {
	private state = $state<ConfirmationState>({
		visible: false,
		title: 'Confirm Action',
		message: 'Are you sure?',
		content: null,
		confirmText: 'Confirm',
		cancelText: 'Cancel',
		confirmButtonType: 0,
		onConfirm: null,
		onCancel: null
	})

	private pendingResolve: ((value: boolean) => void) | null = null
	private queue: Array<{ options: ShowOptions; resolve: (value: boolean) => void }> = []

	get visible() {
		return this.state.visible
	}
	get title() {
		return this.state.title
	}
	get message() {
		return this.state.message
	}
	get confirmText() {
		return this.state.confirmText
	}
	get cancelText() {
		return this.state.cancelText
	}
	get confirmButtonType() {
		return this.state.confirmButtonType
	}
	get content() {
		return this.state.content
	}
	get onConfirm() {
		return this.state.onConfirm
	}
	get onCancel() {
		return this.state.onCancel
	}

	show(options: ShowOptions): Promise<boolean> {
		return new Promise((resolve) => {
			if (this.state.visible) {
				this.queue.push({ options, resolve })
			} else {
				this._open(options, resolve)
			}
		})
	}

	private _open(options: ShowOptions, resolve: (value: boolean) => void) {
		this.pendingResolve = resolve
		this.state.visible = true
		this.state.title = options.title ?? 'Confirm Action'
		this.state.message = options.message ?? 'Are you sure?'
		this.state.content = options.content ?? null
		this.state.confirmText = options.confirmText ?? 'Confirm'
		this.state.cancelText = options.cancelText ?? 'Cancel'
		this.state.confirmButtonType = options.confirmButtonType ?? ButtonType.BRAND

		this.state.onConfirm = () => {
			this.pendingResolve = null
			this.hide()
			resolve(true)
		}

		this.state.onCancel = () => {
			this.pendingResolve = null
			this.hide()
			resolve(false)
		}
	}

	hide() {
		const wasVisible = this.state.visible
		if (this.pendingResolve) {
			this.pendingResolve(false)
			this.pendingResolve = null
		}
		this.state.visible = false
		this.state.content = null
		this.state.onConfirm = null
		this.state.onCancel = null

		// Only dequeue on the first hide() call (when the dialog was actually open).
		// The second hide() triggered by onclose in the layout will see visible=false
		// and skip this, preventing the next dialog from opening twice.
		if (wasVisible) {
			const next = this.queue.shift()
			if (next) {
				this._open(next.options, next.resolve)
			}
		}
	}
}

export const confirmationStore = new ConfirmationStore()
